import React, { useState } from "react";

interface InsuranceProvider {
  name: string;
  url: string;
  logo: string;
}

const insuranceProviders: InsuranceProvider[] = [
  { name: "Medibank", url: "https://www.medibank.com.au/", logo: "/logos/medibank.webp" },
  { name: "Bupa", url: "https://www.bupa.com.au/", logo: "/logos/bupa.webp" },
  { name: "HBF", url: "https://www.hbf.com.au/", logo: "/logos/hbf.webp" },
  { name: "HCF", url: "https://www.hcf.com.au/", logo: "/logos/hcf.webp" },
  { name: "Australian Unity", url: "https://www.australianunity.com.au/", logo: "/logos/australian-unity.webp" },
  { name: "CBHS", url: "https://www.cbhs.com.au/", logo: "/logos/cbhs.webp" },
  { name: "nib", url: "https://www.nib.com.au/", logo: "/logos/nib.webp" },
  { name: "Navy Health", url: "https://navyhealth.com.au/", logo: "/logos/navy-health.webp" },
  { name: "St. Lukes Health", url: "https://stlukes.com.au/", logo: "/logos/st-lukes-health.webp" },
  { name: "Defence Health", url: "https://www.defencehealth.com.au/", logo: "/logos/defence-health.webp" },
  { name: "GMHBA", url: "https://www.gmhba.com.au/", logo: "/logos/gmhba.webp" },
  { name: "Frank Health", url: "https://www.frankhealthinsurance.com.au/", logo: "/logos/frank-health.webp" },
  { name: "Teachers Health", url: "https://www.teachershealth.com.au/", logo: "/logos/teachers-health.webp" },
  { name: "HIF", url: "https://www.hif.com.au/", logo: "/logos/hif.webp" },
  { name: "ahm", url: "https://ahm.com.au/", logo: "/logos/ahm.webp" },
  { name: "GU Health", url: "https://www.guhealth.com.au/", logo: "/logos/gu-health.webp" },
  { name: "Peoplecare", url: "https://www.peoplecare.com.au/", logo: "/logos/peoplecare.webp" },
  { name: "Westfund", url: "https://www.westfund.com.au/", logo: "/logos/westfund.webp" },
];

const ProviderLogo: React.FC<{ name: string; logo: string }>= ({ name, logo }) => {
  const [hasError, setHasError] = useState(false);
  return (
    <div className="flex items-center justify-center h-24 px-10 bg-white rounded-lg border border-neutral-200 hover:border-primary hover:shadow-md transition-all duration-300 group-hover:scale-105">
      {hasError ? (
        <span className="text-sm font-semibold text-neutral-700 whitespace-nowrap">
          {name}
        </span>
      ) : (
        <img
          src={logo}
          alt={name}
          width={160}
          height={64}
          loading="lazy"
          decoding="async"
          className="h-16 w-auto object-contain select-none"
          onError={() => setHasError(true)}
        />
      )}
    </div>
  );
};

const HealthInsuranceTicker = () => {
  const [isPaused, setIsPaused] = useState(false);

  return (
    <section className="bg-gradient-to-r from-neutral-50 via-white to-neutral-50 py-16 overflow-hidden border-y border-neutral-100">
      <div className="container mx-auto px-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold text-primary uppercase tracking-widest mb-4">
            We Accept All Major Health Funds
          </h2>
          <h3 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-2">
            Claim on the spot with
          </h3>
          <div className="flex justify-center mb-4">
            <img
              src="/hicaps-logo.svg"
              alt="HICAPS"
              width={283}
              height={40}
              className="h-10 w-auto"
              loading="lazy"
              decoding="async"
            />
          </div>
          <p className="text-neutral-800 max-w-2xl mx-auto">We work with 100% of Australia's private health insurance providers.</p>
        </div>

        {/* CSS-animated ticker with manual drag/scroll */}
        <div className="relative">
          {/* Gradient overlays for fade effect */}
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

          <div
            role="region"
            aria-label="Health funds logos ticker"
            className="overflow-x-auto overflow-y-hidden py-2 cursor-grab"
            style={{ WebkitOverflowScrolling: 'touch' }}
            tabIndex={0}
            onMouseDown={e => {
              const el = e.currentTarget;
              const startX = e.pageX - el.scrollLeft;
              let isDown = true;
              const onMove = (ev: MouseEvent) => {
                if (!isDown) return;
                el.scrollLeft = ev.pageX - startX;
              };
              const onUp = () => { isDown = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
              window.addEventListener('mousemove', onMove);
              window.addEventListener('mouseup', onUp);
            }}
            onTouchStart={e => {
              const el = e.currentTarget;
              const startX = e.touches[0].pageX - el.scrollLeft;
              let isDown = true;
              const onMove = (ev: TouchEvent) => {
                if (!isDown) return;
                el.scrollLeft = ev.touches[0].pageX - startX;
              };
              const onUp = () => { isDown = false; window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onUp); };
              window.addEventListener('touchmove', onMove);
              window.addEventListener('touchend', onUp);
            }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div 
              className={`flex items-center gap-10 ${isPaused ? '' : 'animate-ticker'}`}
              style={{ width: 'max-content', minHeight: '120px' }}
            >
              {/* Duplicate the list for seamless looping */}
              {[...insuranceProviders, ...insuranceProviders].map((provider, index) => (
                <div
                  key={`${provider.name}-${index}`}
                  className="inline-flex group flex-shrink-0"
                  aria-label={provider.name}
                >
                  <ProviderLogo name={provider.name} logo={provider.logo} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HealthInsuranceTicker;
